import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useId, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { RootState } from "../store/store";
import CategoryEdit from "../components/category/CategoryEdit";
import {
  setUserLanguage,
  setUserCurrency,
  setCategory,
} from "../store/appSlice";
import { AlertMessage } from "../components/AlertMessage";
import request from "../apis/request";
import { APIResponse } from "../types/apiTypes";
import { Category, CategoryItem, CategoryOtherLangs, User } from "../types";
import { useTranslation } from "react-i18next";
import { changeCategoryLabel } from "../utils/utilities";

const Setting = () => {
  const langId = useId();
  const currId = useId();
  const { isLoggedIn, category, userInfo } = useSelector(
    (state: RootState) => state.app,
  );
  const dispatch = useDispatch();
  const [showPopup, setShowPopup] = useState(false);
  const [viewAlert, setViewAlert] = useState(false);
  const { t: tCommon } = useTranslation("common");
  const { t: tError } = useTranslation("errors");

  const [userLang, setUserLang] = useState<string | undefined>(
    userInfo?.language || undefined,
  );
  const [userCurren, setUserCurren] = useState<string | undefined>(
    userInfo?.currency || undefined,
  );

  const navigate = useNavigate();

  useEffect(() => {
    if (isLoggedIn) {
      setViewAlert(false);
      if (
        (userInfo &&
          !(userInfo?.language && userInfo.language !== "undefined")) ||
        !(userInfo?.currency && userInfo.currency !== "undefined")
      ) {
        setShowPopup(true);
      }
    } else {
      setViewAlert(true);
    }
  }, [isLoggedIn, userInfo?.currency, userInfo?.language]);

  const handleAlertClose = (type: string) => {
    if (type === "login") {
      setViewAlert(false);
      navigate("/login");
      return;
    }

    setShowPopup(false);
  };

  const handleUserLangChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserLang(event?.target.value);
  };

  const handleSaveLanguage = async () => {
    if (!userLang) return;
    try {
      const response = await request<APIResponse<User>>({
        method: "PUT",
        url: "saveLanguage",
        requireAuth: true,
        data: { language: userLang },
        token: userInfo?.token,
      });

      console.log(`set : state: ${userInfo?.language} - ${userLang}`);
      if (
        (userInfo?.language && userInfo.language !== userLang) ||
        (!userInfo?.language && userLang !== "ja")
      ) {
        console.log(`入替：state: ${userInfo?.language} - ${userLang}`);
        // labelの入替
        if (category) {
          const changedCategory = changeCategoryLabel(userLang, category);
          dispatch(setCategory(changedCategory));
        }
      }

      dispatch(setUserLanguage(userLang));
    } catch (error: any) {
      console.error(error.message, error);

      alert("言語設定の保存に失敗しました。");
    }
  };

  const handleUserCurrChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserCurren(event?.target.value);
  };

  const handleSaveCurrency = async () => {
    if (!userCurren) return;

    try {
      const response = await request<APIResponse<User>>({
        method: "PUT",
        url: "saveCurrency",
        requireAuth: true,
        data: { currency: userCurren },
        token: userInfo?.token,
      });

      dispatch(setUserCurrency(userCurren));
    } catch (error: any) {
      console.error(error.message, error);

      alert("通貨設定の保存に失敗しました。");
    }
  };

  const handleCloseAlert = () => {
    setViewAlert(false);
  };

  // const changeCategoryLabel = (lang: string): Category => {
  //   if (!category) return {} as Category;

  //   const { userId, expense: expenses, income: incomes } = category;

  //   const income = incomes.map((t: CategoryItem) => ({
  //     ...t,
  //     label: t.otherLangs?.find((tl) => tl.lang === lang)?.label as string,
  //   }));
  //   const expense = expenses.map((t) => ({
  //     ...t,
  //     label: t.otherLangs?.find((tl) => tl.lang === lang)?.label as string,
  //   }));

  //   return {
  //     userId,
  //     expense: expense || ([] as CategoryItem[]),
  //     income: income || ([] as CategoryItem[]),
  //   };
  // };

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 1.5, sm: 3 }, py: 2 }}>
      <Stack spacing={2}>
        {/* 言語選択 */}
        <Box
          component="section"
          sx={{
            bgcolor: "#fff",
            border: "1px solid #e5e5e5",
            borderRadius: 2,
            boxShadow: "0 1px 5px rgba(0, 0, 0, 0.12)",
            p: { xs: 1.5, sm: 2 },
          }}
        >
          <Stack spacing={1.5}>
            <Typography component="h2" variant="h6" sx={{ mb: 1.5 }}>
              言語(언어,Language)
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <FormControl sx={{ marginLeft: 2 }}>
                <RadioGroup
                  row
                  aria-labelledby={`${langId}-label`}
                  // defaultValue={userInfo?.language}
                  value={userLang}
                  name="radio-buttons-group"
                  onChange={handleUserLangChange}
                >
                  <FormControlLabel
                    value="ja"
                    control={<Radio />}
                    label="日本語"
                  />
                  <FormControlLabel
                    value="ko"
                    control={<Radio />}
                    label="한국어"
                  />
                  <FormControlLabel
                    value="en"
                    control={<Radio />}
                    label="English"
                  />
                </RadioGroup>
              </FormControl>
              <Button variant="contained" onClick={handleSaveLanguage}>
                {tCommon("actions.save")}
              </Button>
            </Box>
          </Stack>
        </Box>

        {/* 通貨選択 */}
        <Box
          component="section"
          sx={{
            bgcolor: "#fff",
            border: "1px solid #e5e5e5",
            borderRadius: 2,
            boxShadow: "0 1px 5px rgba(0, 0, 0, 0.12)",
            p: { xs: 1.5, sm: 2 },
          }}
        >
          <Stack spacing={1.5}>
            <Typography component="h2" variant="h6" sx={{ mb: 1.5 }}>
              通貨(통화,Currency)
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <FormControl sx={{ marginLeft: 2 }}>
                <RadioGroup
                  row
                  aria-labelledby={`${currId}-label`}
                  value={userCurren}
                  name="radio-buttons-group"
                  onChange={handleUserCurrChange}
                >
                  <FormControlLabel
                    value="jpy"
                    control={<Radio />}
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <Typography sx={{ fontWeight: 500, color: "#333" }}>
                          円
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="krw"
                    control={<Radio />}
                    label="원"
                  />
                  <FormControlLabel
                    value="usd"
                    control={<Radio />}
                    label="Dollar"
                  />
                </RadioGroup>
              </FormControl>
              <Button variant="contained" onClick={handleSaveCurrency}>
                保存
              </Button>
            </Box>
          </Stack>
        </Box>

        {isLoggedIn && (
          <Box
            component="section"
            sx={{
              bgcolor: "#fff",
              border: "1px solid #e5e5e5",
              borderRadius: 2,
              boxShadow: "0 1px 5px rgba(0, 0, 0, 0.12)",
              p: { xs: 1.5, sm: 2 },
            }}
          >
            <NavLink to={"/change-password"}>
              <Typography component="h2" variant="h6" sx={{ mb: 1.5 }}>
                {tCommon("auth.changePassword")}
              </Typography>
            </NavLink>
          </Box>
        )}
        <CategoryEdit />
      </Stack>
      {!isLoggedIn ? (
        <AlertMessage
          open={viewAlert}
          type={"warn"}
          errorCode="warn.NotLoggedIn"
          returnType={"login"}
          onResult={handleAlertClose}
          handleClose={handleCloseAlert}
        ></AlertMessage>
      ) : (
        <AlertMessage
          open={showPopup}
          type={"warn"}
          returnType={""}
          errorCode="warn.UnsetLocale"
          onResult={handleAlertClose}
          handleClose={handleCloseAlert}
        ></AlertMessage>
      )}
    </Box>
  );
};

export default Setting;
